import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Sparkles, ArrowRight, X, Loader2, Truck, Store, Utensils, Package } from "lucide-react";
import { Button } from "../ui/button";
import api from "../../lib/api";

const TYPE_ICONS = {
  mayorista: Truck,
  supermercado: Store,
  especialista: Utensils,
  distribuidor: Package,
  otros: Package,
};

const TYPE_COLORS = {
  mayorista: "bg-amber-50 text-amber-700 border-amber-200",
  supermercado: "bg-sky-50 text-sky-700 border-sky-200",
  especialista: "bg-emerald-50 text-emerald-700 border-emerald-200",
  distribuidor: "bg-violet-50 text-violet-700 border-violet-200",
  otros: "bg-muted/50 text-foreground border-border",
};

const POLL_INTERVAL_MS = 3000;
const POLL_MAX_ATTEMPTS = 10; // ~30s
const MIN_SUPPLIERS_TO_SHOW = 3;
const MAX_SUPPLIERS_DISPLAY = 6;

const dismissedKey = (rid) => `welcome-suppliers-dismissed-${rid}`;

/**
 * Shown once after a user finishes onboarding: polls suppliers until the
 * AI auto-seed has created enough entries, then reveals a gradient banner
 * showcasing the recommended providers as a conversion hook for the
 * Inventory module.
 */
export default function WelcomeSuppliersBanner({ restaurantId, onDismiss }) {
  const navigate = useNavigate();
  const [suppliers, setSuppliers] = useState([]);
  const [status, setStatus] = useState("loading"); // loading | ready | dismissed | timeout
  const attemptRef = useRef(0);
  const timerRef = useRef(null);

  // Restore dismissed state from localStorage
  useEffect(() => {
    if (!restaurantId) return;
    try {
      if (localStorage.getItem(dismissedKey(restaurantId)) === "1") {
        setStatus("dismissed");
      }
    } catch { /* no-op */ }
  }, [restaurantId]);

  // Polling loop
  useEffect(() => {
    if (!restaurantId || status === "dismissed" || status === "timeout") return;

    let cancelled = false;

    const tick = async () => {
      attemptRef.current += 1;
      try {
        const all = await api.getSuppliers(restaurantId);
        const auto = (Array.isArray(all) ? all : [])
          .filter(s => s?.auto_suggested === true)
          .slice(0, MAX_SUPPLIERS_DISPLAY);
        if (cancelled) return;
        if (auto.length >= MIN_SUPPLIERS_TO_SHOW) {
          setSuppliers(auto);
          setStatus("ready");
          return;
        }
      } catch {
        /* Silent — keep polling */
      }
      if (cancelled) return;
      if (attemptRef.current >= POLL_MAX_ATTEMPTS) {
        setStatus("timeout");
        return;
      }
      timerRef.current = setTimeout(tick, POLL_INTERVAL_MS);
    };

    // First attempt almost immediately; seed is fire-and-forget, can take 5-15s
    timerRef.current = setTimeout(tick, 1500);

    return () => {
      cancelled = true;
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [restaurantId, status]);

  const handleDismiss = () => {
    try {
      if (restaurantId) localStorage.setItem(dismissedKey(restaurantId), "1");
    } catch { /* no-op */ }
    setStatus("dismissed");
    onDismiss?.();
  };

  const handleViewAll = () => {
    handleDismiss();
    navigate("/app/suppliers");
  };

  if (status === "dismissed" || status === "timeout") return null;

  return (
    <div
      className="relative overflow-hidden rounded-2xl border border-violet-200/60 bg-gradient-to-br from-violet-50 via-fuchsia-50 to-amber-50 p-5 shadow-sm"
      data-testid="welcome-suppliers-banner"
    >
      {/* Decorative blur orbs */}
      <div className="pointer-events-none absolute -top-16 -right-16 h-40 w-40 rounded-full bg-violet-200/40 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-20 -left-10 h-40 w-40 rounded-full bg-amber-200/40 blur-3xl" />

      <button
        onClick={handleDismiss}
        className="absolute top-3 right-3 p-1.5 rounded-full hover:bg-white/60 transition-colors"
        aria-label="Cerrar"
        data-testid="welcome-suppliers-dismiss"
      >
        <X className="h-4 w-4 text-muted-foreground" />
      </button>

      <div className="relative flex items-start gap-3 mb-4">
        <div className="shrink-0 h-10 w-10 rounded-xl bg-gradient-to-br from-violet-500 to-fuchsia-500 flex items-center justify-center shadow-sm">
          <Sparkles className="h-5 w-5 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          {status === "loading" ? (
            <>
              <h3 className="text-base font-semibold text-foreground flex items-center gap-2">
                Buscando proveedores cerca de ti
                <Loader2 className="h-4 w-4 animate-spin text-violet-600" />
              </h3>
              <p className="text-sm text-muted-foreground mt-0.5">
                Nuestra IA está seleccionando mayoristas, distribuidores y supermercados recomendados para tu cocina…
              </p>
            </>
          ) : (
            <>
              <h3 className="text-base font-semibold text-foreground">
                ¡Bienvenido! Hemos detectado {suppliers.length} proveedores recomendados cerca de ti
              </h3>
              <p className="text-sm text-muted-foreground mt-0.5">
                Revísalos, ajusta contactos y empieza a pedir. Puedes añadir más cuando quieras.
              </p>
            </>
          )}
        </div>
      </div>

      {status === "loading" && (
        <div className="relative grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
          {[0, 1, 2].map(i => (
            <div
              key={i}
              className="h-16 rounded-lg bg-card/60 border border-white animate-pulse"
              data-testid={`welcome-suppliers-skeleton-${i}`}
            />
          ))}
        </div>
      )}

      {status === "ready" && (
        <>
          <div className="relative grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {suppliers.map(s => {
              const type = s.category_hint || "otros";
              const Icon = TYPE_ICONS[type] || Package;
              const addressHint = s.address ? s.address : null;
              return (
                <div
                  key={s.supplier_id}
                  className="flex items-start gap-2.5 rounded-lg bg-card/80 border border-white px-3 py-2 shadow-xs hover:shadow-sm transition-shadow"
                  data-testid={`welcome-supplier-${s.supplier_id}`}
                >
                  <div className="shrink-0 h-8 w-8 rounded-lg bg-muted/50 border border-border flex items-center justify-center">
                    <Icon className="h-4 w-4 text-muted-foreground" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-foreground truncate">{s.name}</div>
                    <div className="flex items-center gap-1.5 mt-0.5">
                      <span className={`inline-flex items-center text-[10px] font-medium px-1.5 py-0.5 rounded border ${TYPE_COLORS[type] || TYPE_COLORS.otros}`}>
                        {type}
                      </span>
                      {addressHint && (
                        <span className="text-[11px] text-muted-foreground truncate">{addressHint}</span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="relative mt-4 flex items-center justify-end">
            <Button
              size="sm"
              onClick={handleViewAll}
              className="bg-gradient-to-r from-violet-600 to-fuchsia-600 hover:from-violet-700 hover:to-fuchsia-700 text-white shadow-sm"
              data-testid="welcome-suppliers-view-all"
            >
              Ver todos los proveedores
              <ArrowRight className="h-4 w-4 ml-1.5" />
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
